/**
 * RBP: quick view media.
 *
 * Handles two things:
 *   1. Clicking a thumbnail swaps the main image.
 *   2. Changing a variant inside the quick view re-filters both the main
 *      images and the thumbnails, so only the selected variant's media show.
 *
 * Filtering mirrors the Liquid and the product page: an image whose alt text
 * matches one of this product's option values belongs to that variant; blank
 * or non-matching alt text is a shared image and always shows.
 *
 * Defined as a custom element so it upgrades automatically when the quick view
 * markup is injected by the drawer's fetch, with no hook into that loader.
 */
class QuickViewMedia extends HTMLElement {
  connectedCallback() {
    this.mainItems = () => Array.from(this.querySelectorAll(".quick-view-media__item"));
    this.thumbs = () => Array.from(this.querySelectorAll(".quick-view-media__thumb"));

    this.addEventListener("click", event => {
      const thumb = event.target.closest(".quick-view-media__thumb");
      if (thumb) this.setActive(thumb.dataset.mediaId);
    });

    // The variant radios live outside this element, in the quick view body.
    this.root = this.closest(".quick-cart-product") || document;
    this.onChange = this.onChange.bind(this);
    this.root.addEventListener("change", this.onChange);
  }

  disconnectedCallback() {
    if (this.root) this.root.removeEventListener("change", this.onChange);
  }

  onChange(event) {
    if (!event.target.closest || !event.target.closest(".product-option__input, .product-card__variants")) return;
    // Let the theme's own variant handling settle first.
    setTimeout(() => this.filter(), 50);
  }

  /** Option values currently selected in the quick view. */
  selectedValues() {
    const values = new Set();
    this.root.querySelectorAll(".product-option__input:checked").forEach(input => {
      if (input.value) values.add(input.value.trim().toLowerCase());
    });
    return values;
  }

  /** Every option value on this product, used to spot shared images. */
  allOptionValues() {
    const values = new Set();
    this.root.querySelectorAll(".product-option__input").forEach(input => {
      if (input.value) values.add(input.value.trim().toLowerCase());
    });
    return values;
  }

  filter() {
    const selected = this.selectedValues();
    if (selected.size === 0) return;
    const known = this.allOptionValues();

    const visible = el => {
      const group = (el.dataset.mediaGroup || "").trim().toLowerCase();
      const isVariantTag = known.has(group);
      return group === "" || !isVariantTag || selected.has(group);
    };

    let firstVisibleId = null;
    this.mainItems().forEach(item => {
      const show = visible(item);
      item.classList.toggle("hidden", !show);
      if (show && !firstVisibleId) firstVisibleId = item.dataset.mediaId;
    });

    this.thumbs().forEach(thumb => {
      thumb.classList.toggle("hidden", !visible(thumb));
    });

    if (firstVisibleId) this.setActive(firstVisibleId);
  }

  setActive(mediaId) {
    if (!mediaId) return;
    this.mainItems().forEach(item => {
      item.classList.toggle("is-active", item.dataset.mediaId === mediaId);
    });
    this.thumbs().forEach(thumb => {
      thumb.classList.toggle("is-active", thumb.dataset.mediaId === mediaId);
    });
  }
}

if (!customElements.get("quick-view-media")) {
  customElements.define("quick-view-media", QuickViewMedia);
}

/**
 * RBP: opt-in diagnostic. Add ?rbpqv=1 to any collection URL, open a quick
 * view, and a readout appears describing the main image element. Off unless
 * that parameter is present, so ordinary visitors never see it.
 */
if (new URLSearchParams(location.search).has("rbpqv")) {
  document.addEventListener("click", function () {
    setTimeout(function () {
      var root = document.querySelector("quick-view-media");
      if (!root || root.querySelector(".rbp-qv-probe")) return;

      var items = root.querySelectorAll(".quick-view-media__item");
      var active = root.querySelector(".quick-view-media__item.is-active") || items[0];
      var img = active ? active.querySelector("img") : null;
      var box = function (el) {
        if (!el) return "none";
        var r = el.getBoundingClientRect();
        var c = getComputedStyle(el);
        return Math.round(r.width) + "x" + Math.round(r.height) + " disp:" + c.display;
      };

      var lines = [
        "items=" + items.length + " active=" + (active ? "yes" : "NO"),
        "item " + box(active),
        "media " + box(active && active.querySelector(".media")),
        "img " + box(img),
        img ? "complete=" + img.complete + " natural=" + img.naturalWidth + "x" + img.naturalHeight : "img MISSING",
        img ? "currentSrc=" + String(img.currentSrc || img.src).slice(-60) : ""
      ];

      var d = document.createElement("div");
      d.className = "rbp-qv-probe";
      d.style.cssText =
        "background:#111;color:#0f0;font:10px/1.5 monospace;padding:8px;white-space:pre-wrap;word-break:break-all";
      d.textContent = lines.join("\n");
      root.appendChild(d);
    }, 2500);
  });
}

